// client/components/forms/shared/FormAtoms.jsx
// Shared form primitives: Input, Textarea, Field, Tag, MiniPlus
import React from "react";
import { twMerge } from "tailwind-merge";

const baseFieldClass =
  "w-full min-w-0 border border-default bg-surface-elevated text-primary placeholder:text-muted px-3 py-2 rounded text-sm max-md:text-base md:text-sm focus:outline-none focus-ring transition-all min-h-[44px] md:min-h-0";

/* ─── Text Input ──────────────────────────────────────────────────── */
export const Input = React.forwardRef(function Input({ className, ...props }, ref) {
  return <input ref={ref} {...props} className={twMerge(baseFieldClass, className)} />;
});

/* ─── Textarea ────────────────────────────────────────────────────── */
export const Textarea = React.forwardRef(function Textarea({ rows = 3, className, style, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...props}
      style={{ minHeight: `max(calc(${rows} * 1.5rem), 44px)`, ...style }}
      className={twMerge(baseFieldClass, "resize-y", className)}
    />
  );
});

/* ─── Select ──────────────────────────────────────────────────────── */
export const Select = React.forwardRef(function Select({ className, style, ...props }, ref) {
  return (
    <select
      ref={ref}
      {...props}
      style={{ colorScheme: "dark", ...style }}
      className={twMerge(baseFieldClass, className)}
    />
  );
});

/* ─── Checkbox ────────────────────────────────────────────────────── */
export const Checkbox = React.forwardRef(function Checkbox({ className, ...props }, ref) {
  return (
    <input
      ref={ref}
      type="checkbox"
      {...props}
      className={twMerge(
        "shrink-0 rounded border border-white/10 bg-surface-elevated accent-[var(--primary)] h-4 w-4 max-md:h-5 max-md:w-5 md:h-4 md:w-4",
        className,
      )}
    />
  );
});

/* ─── Field (read-only info display) ──────────────────────────────── */
export function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wide text-blue-300/60">{label}</div>
      <div className="mt-0.5 break-words">{value}</div>
    </div>
  );
}

/* ─── Tag (editable badge with remove button) ─────────────────────── */
export function Tag({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2 py-1 text-[11px]">
      {children}
      <button
        type="button"
        className="text-blue-200/70 hover:text-blue-100 px-1 rounded"
        onClick={onRemove}
        title="Entfernen"
        aria-label="Tag entfernen"
      >
        ✕
      </button>
    </span>
  );
}

/* ─── MiniPlus (small inline add-button) ──────────────────────────── */
export function MiniPlus({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      title={title}
      className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-[11px] leading-none text-blue-100/80 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
    >
      +
    </button>
  );
}

/* ─── CalendarIcon (inline SVG) ───────────────────────────────────── */
export function CalendarIcon({ className = "w-3.5 h-3.5 shrink-0 opacity-80" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M7 3v3M17 3v3M3.5 9.5h17M5 6.5h14a1.5 1.5 0 0 1 1.5 1.5v10A2.5 2.5 0 0 1 18 20.5H6A2.5 2.5 0 0 1 3.5 18V8A1.5 1.5 0 0 1 5 6.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ─── KundeMini (compact customer display for cards) ──────────────── */
export function KundeMini({
  firma,
  vorname,
  nachname,
  strasse,
  plz,
  ort,
  verwaltung_text,
}) {
  const kontakt = [vorname, nachname].filter(Boolean).join(" ").trim();
  const ortLine = [plz, ort].filter(Boolean).join(" ").trim();
  const verw = (verwaltung_text || "").split("\n").filter(Boolean)[0] || "";
  return (
    <div className="text-[13px] leading-tight text-blue-100/90">
      {firma?.trim() && <div className="font-medium text-blue-100">{firma}</div>}
      {kontakt && <div className="text-blue-100/90">{kontakt}</div>}
      {(strasse || ortLine) && (
        <div className="transition-colors text-blue-200/70 group-hover:text-blue-200/80">
          {[strasse, ortLine].filter(Boolean).join(", ")}
        </div>
      )}
      {verw && (
        <div className="mt-1 text-blue-200/70">
          <span className="font-medium text-blue-100/90">Verwaltung:</span> {verw}
        </div>
      )}
    </div>
  );
}

/* ─── Section Divider (for form modules) ──────────────────────────── */
export function SectionDivider({ label, icon }) {
  return (
    <div className="flex items-center gap-2 pt-3 pb-1 border-t border-white/10">
      {icon && <span className="opacity-60">{icon}</span>}
      <span className="text-xs font-semibold uppercase tracking-wide text-blue-300/70">{label}</span>
    </div>
  );
}
